// The app's automation engine: ticked on the session clock's `automation` stage, writing samples to Pixi objects and to a wire overlay.
import { bandLevel, needAudio } from '~/lib/audio/bands'
import { onTick } from '~/lib/clock'
import tempo from '~/lib/clock/tempo'
import { IS_DEV } from '~/lib/constants'
import { classifyParam, parseLivePath, schemaForLivePath } from '~/lib/params'
import { applyObjectWrite } from '~/lib/objectWrites'
import { shownSourceValues } from '~/lib/sourceWrites'
import { withDrafts } from './drafts'
import { createEngine } from './engine'

let engine = null
let overlay = {}
// The runner's samples as this peer last received them (plan C1); empty on the runner.
let remote = new Map()
// The server takes samples as their own message; false: they ride `parameters` (a server naming no runner).
let samplesOnWire = false
const listeners = new Set()

export const setSamplesOnWire = on => {
	samplesOnWire = on
}

export const isSamplesOnWire = () => samplesOnWire

/** Effect samples as `{ effect: { key: value } }`, for the wire only. */
export const automationOverlay = () => overlay

/** Called after every tick that produced or cleared samples, local or received. */
export const onAutomationSample = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

/** Null on every client but the runner. */
export const getEngine = () => engine

/** The latest sample per looped path: this client's own, else the runner's. */
export const currentSamples = () => engine?.samples() ?? remote

/** The moving value of a looped `path`, from either source. */
export const sampleOf = path => currentSamples().get(path)

// The objects the last sink drew from: a group's W/H sample is a scale over its children's bbox.
let sinkObjects = {}

const objectWithSamples = (obj, entries) => {
	const o = { ...obj }
	// A vector's or group's W/H is derived geometry: the sample takes the panel's write, one axis alone.
	const derive = ({ payload: { id: _id, ...props } }) => Object.assign(o, props)
	for (const [p, v] of entries) {
		if ((p.key === 'width' || p.key === 'height') && applyObjectWrite(derive, o, sinkObjects, p.key, v)) continue
		o[p.key] = v
	}
	return o
}

// A canvas drag's uncommitted props per object id. Redux holds the pre-drag pose until pointer-up, so every draw lays these over it — else each tick snaps the object back.
const held = new Map()

const objectEntries = id => {
	const entries = []
	for (const [path, v] of currentSamples()) {
		const p = parseLivePath(path)
		if (p?.scope === 'object' && p.id === id) entries.push([p, v])
	}
	return entries
}

/** What a drag draws for object `id`: Redux `obj`, its samples, then the drag's `props`, which stay held until `releaseObjects`. */
export const holdObject = (id, obj, props) => {
	held.set(id, props)
	return { ...objectWithSamples(obj, objectEntries(id)), ...props }
}

/** Call once the drag has dispatched (or abandoned) its props. */
export const releaseObjects = () => held.clear()

const renderObject = (id, obj, entries = []) => {
	const entry = window.pixi_objects_map?.get(id)
	if (entry && obj && window.pixi_update_object)
		window.pixi_update_object(entry.pixiObj, { ...objectWithSamples(obj, entries), ...held.get(id) })
}

// Mid-fade the tweens own the pose and Redux holds the FROM scene, so only sampled props are written — to an object the fade is creating too.
const writeDuringFade = (id, entries) => {
	const pixiObj = window.pixi_objects_map?.get(id)?.pixiObj ?? window.pixi_fade_owned?.(id)
	if (!pixiObj || pixiObj.destroyed) return
	for (const [p, v] of entries) {
		if (p.key === 'opacity') pixiObj.alpha = v / 100
		else if (p.key === 'x' || p.key === 'y') pixiObj[p.key] = v
	}
}

// A source's samples land on every fill node showing it, over the bag on screen; mid-fade too, where the fade's blend of that value yields to the loop.
const renderSource = (source, values) => window.pixi_write_source?.(source, { ...shownSourceValues(source), ...values })

const sink = (samples, prev, live) => {
	const effects = {}
	const objects = new Map()
	const sources = new Map()
	for (const [path, value] of samples) {
		const p = parseLivePath(path)
		if (p.scope === 'effect') {
			const integer = classifyParam(schemaForLivePath(path, live.objects)).integer
			;(effects[p.effect] ??= {})[p.key] = integer ? Math.round(value) : value
		} else if (p.scope === 'source') {
			;(sources.get(p.source) ?? sources.set(p.source, {}).get(p.source))[p.key] = value
		} else {
			if (!objects.has(p.id)) objects.set(p.id, [])
			objects.get(p.id).push([p, value])
		}
	}
	overlay = effects
	sinkObjects = live.objects ?? {}
	for (const [id, entries] of objects) {
		if (window.pixi_transition_active) writeDuringFade(id, entries)
		else renderObject(id, live.objects[id], entries)
	}
	for (const [source, values] of sources) renderSource(source, values)
	// An object or source whose loops all ended goes back to what Redux says, once.
	for (const path of prev.keys()) {
		const p = parseLivePath(path)
		if (p.scope === 'object' && !objects.has(p.id)) renderObject(p.id, live.objects?.[p.id])
		else if (p.scope === 'source' && !sources.has(p.source)) renderSource(p.source)
	}
	for (const fn of listeners) fn()
}

// A colour param's sample.
const HEX = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i

/** A peer: the runner's whole sample set. Drawn like the runner's own (the streamed stage may be this one), never stored. */
export function receiveSamples(store, payload) {
	if (engine) return
	const next = new Map()
	if (payload && typeof payload === 'object')
		for (const [path, v] of Object.entries(payload)) {
			const p = parseLivePath(path)
			if (p && !p.existence && (Number.isFinite(v) || HEX.test(v))) next.set(path, v)
		}
	const prev = remote
	remote = next
	sink(next, prev, store.getState().live)
}

/** A peer whose runner changed or left: the last runner's samples come off the stage. */
export const clearRemoteSamples = store => receiveSamples(store, null)

/** Start the engine on the session clock; returns the stop. Runner only (plan A6, C1). */
export function startAutomation(store) {
	clearRemoteSamples(store)
	engine = createEngine({
		// A gesture's draft drives the loop before Redux holds it.
		getState: () => withDrafts(store.getState()),
		clock: tempo,
		sink,
		audio: { level: bandLevel, need: needAudio },
	})
	const off = onTick('automation', engine.tick)
	if (IS_DEV) window.automation = engine
	return () => {
		off()
		const live = store.getState().live
		for (const path of engine.samples().keys()) {
			const p = parseLivePath(path)
			if (p.scope === 'object') renderObject(p.id, live.objects?.[p.id])
			else if (p.scope === 'source') renderSource(p.source)
		}
		overlay = {}
		needAudio(false)
		// Before the listeners: the wire sender reads a stopped engine as "clear the server's samples".
		engine = null
		for (const fn of listeners) fn()
		if (IS_DEV) window.automation = null
	}
}
